import { useLang } from "@/context/LanguageContext";
import { useSectionMeta } from "@/context/SiteConfigContext";
import { assetUrl } from "@/lib/api";
import { MaskWords, SectionHead, SectionButtons } from "@/components/site/primitives";
import { Reveal } from "@/components/site/Reveal";

function T(val, lang) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  return val[lang] || val.ro || val.en || "";
}

export default function CustomSection({ id }) {
  const { lang } = useLang();
  const meta = useSectionMeta(id);
  const dark = meta.theme === "dark";
  const blocks = meta.blocks || [];
  const label = T(meta.navLabel, lang);

  return (
    <section
      id={id}
      data-testid={`custom-section-${id}`}
      className={`${dark ? "bg-noir text-paper" : "bg-paper text-ink"} px-6 md:px-12 lg:px-16 py-28 md:py-44`}
    >
      {label && <SectionHead dark={dark} label={label} index={meta.index || ""} />}

      <div className="mt-12 md:mt-16 space-y-14 md:space-y-24">
        {blocks.map((b, i) => (
          <Block key={b.id || i} block={b} lang={lang} dark={dark} />
        ))}
      </div>
    </section>
  );
}

function Block({ block, lang, dark }) {
  const type = block.type;

  if (type === "heading") {
    return (
      <MaskWords
        Tag="h2"
        text={T(block.text, lang) || " "}
        className="display uppercase text-[11vw] lg:text-[7.5vw] leading-[0.85] break-words"
      />
    );
  }

  if (type === "paragraph") {
    return (
      <Reveal>
        <p className={`text-lg md:text-2xl leading-[1.5] font-medium max-w-3xl ${dark ? "text-paper/85" : "text-ink/90"}`}>
          {T(block.text, lang)}
        </p>
      </Reveal>
    );
  }

  if (type === "quote") {
    return (
      <Reveal>
        <blockquote className="max-w-4xl">
          <p className="display text-3xl md:text-6xl leading-[1.02]">{T(block.text, lang)}</p>
          {T(block.author, lang) && (
            <footer className={`eyebrow mt-6 ${dark ? "text-paper/55" : "text-ink/55"}`}>— {T(block.author, lang)}</footer>
          )}
        </blockquote>
      </Reveal>
    );
  }

  if (type === "image") {
    if (!block.url) return null;
    return (
      <Reveal>
        <figure>
          <div className="overflow-hidden aspect-[16/9] group">
            <img src={assetUrl(block.url)} alt={T(block.caption, lang)} className="duo w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
          </div>
          {T(block.caption, lang) && (
            <figcaption className={`eyebrow mt-3 ${dark ? "text-paper/50" : "text-ink/50"}`}>{T(block.caption, lang)}</figcaption>
          )}
        </figure>
      </Reveal>
    );
  }

  if (type === "fullwidth_image") {
    if (!block.url) return null;
    return (
      <Reveal>
        <div className="-mx-6 md:-mx-12 lg:-mx-16 overflow-hidden aspect-[21/9]">
          <img src={assetUrl(block.url)} alt="" className="w-full h-full object-cover" />
        </div>
      </Reveal>
    );
  }

  if (type === "image_text") {
    const right = !!block.imageRight;
    return (
      <Reveal>
        <div className="grid grid-cols-12 gap-6 md:gap-8 items-center">
          <div className={`col-span-12 lg:col-span-7 ${right ? "lg:order-2" : ""}`}>
            {block.url && (
              <div className="overflow-hidden aspect-[16/10] group">
                <img src={assetUrl(block.url)} alt="" className="duo w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
              </div>
            )}
          </div>
          <div className={`col-span-12 lg:col-span-4 ${right ? "lg:order-1" : "lg:col-start-9"}`}>
            {T(block.heading, lang) && <h3 className="display text-4xl md:text-5xl leading-[0.95]">{T(block.heading, lang)}</h3>}
            {T(block.text, lang) && <p className={`mt-5 leading-[1.6] ${dark ? "text-paper/70" : "text-ink/70"}`}>{T(block.text, lang)}</p>}
          </div>
        </div>
      </Reveal>
    );
  }

  if (type === "buttons") {
    return (
      <Reveal>
        <SectionButtons buttons={block.buttons} dark={dark} />
      </Reveal>
    );
  }

  return null;
}
